// src/lib/pricing-display.js — Phase 6 PR2 (ADR-092)
//
// Helper ESM UI-only : formatters EUR + freshness labels pour calendrier.
// Pas de clone CJS (jamais consommé serveur).
//
// Cf. plan v2 § 6.7.

import { DS } from "./ui";

// ── Verrou sélectif — libellé UNIQUE du repère 📌 ──────────────────────
//
// Texte EXACT validé par Dimitri (2026-08-06). Source unique consommée par
// TableauView (badge + soulignement du prix), CalendarLegend et la modale de
// prix manuel : un seul mot d'ordre à l'écran, jamais deux formulations.
//
// Il dit la VÉRITÉ de l'état actuel (règle « textes d'aide = état actuel ») :
// depuis le verrou sélectif, seul le PRIX est figé — la dispo et les nuits
// minimum, elles, continuent de suivre Beds24.
// ⚠️ Chaîne recopiée MOT POUR MOT de la demande de Dimitri, casse comprise.
// Ne pas « corriger » la majuscule : le texte à l'écran doit être le sien.
export const PRICING_LOCK_LABEL = "prix fixé manuellement, non synchronisé";

// ── countDatesToLock — combien de dates un prix manuel va verrouiller ──
//
// CAUSE RACINE du lot (2026-08-03) : Dimitri a posé un prix du 3 au 31 août et
// verrouillé 69 dates sans que rien ne le lui dise. L'écran de saisie doit
// annoncer le nombre AVANT validation — et il doit être EXACT, pas estimé.
//
// POURQUOI IL EST EXACT SANS ALLER-RETOUR SERVEUR (vérifié dans le code) :
// `pricing_set_manual` reçoit `date_from`/`date_to` et appelle
// `expandRangeToRows(ranges, logement_id, 'manual_push_set')` (lib/pricing-push.js)
// qui itère les dates de `from` à `to` **bornes INCLUSES** et pose
// `is_manual_override = true` sur CHACUNE (lock défaut `true`). Il n'y a ni
// filtre, ni exclusion, ni condition : le nombre de dates verrouillées est
// exactement le nombre de nuits de l'intervalle. Un SELECT serveur ne dirait
// rien de plus.
//
// CE QU'ON N'ANNONCE PAS, ET POURQUOI : on ne découpe PAS en « nouvelles » vs
// « déjà verrouillées ». Le calendrier ne charge que les mois affichés — sur
// une plage débordant l'affichage, ce sous-total serait FAUX. Un chiffre faux
// serait pire que pas de chiffre : on n'annonce que le total, toujours juste.
//
// @param {string} fromISO — YYYY-MM-DD inclus
// @param {string} toISO   — YYYY-MM-DD inclus
// @returns {number|null} nombre de dates, ou null si bornes invalides/inversées
export function countDatesToLock(fromISO, toISO) {
  const re = /^\d{4}-\d{2}-\d{2}$/;
  if (typeof fromISO !== "string" || typeof toISO !== "string") return null;
  if (!re.test(fromISO) || !re.test(toISO)) return null;
  const a = Date.parse(`${fromISO}T00:00:00Z`);
  const b = Date.parse(`${toISO}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  // Roundtrip : refuse les dates normalisées par JS (2026-02-30 → 2026-03-02).
  if (new Date(a).toISOString().slice(0, 10) !== fromISO) return null;
  if (new Date(b).toISOString().slice(0, 10) !== toISO) return null;
  if (b < a) return null;
  // Arithmétique en ms UTC → immune au changement d'heure (DST).
  return Math.round((b - a) / 86400000) + 1;
}

// ── formatEUR ─────────────────────────────────────────────────────────

export function formatEUR(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const n = Number(value);
  // Pas de décimales si entier, sinon 2 décimales (style pricing)
  if (Number.isInteger(n)) return `${n} €`;
  return `${n.toFixed(2)} €`;
}

// Note : `computeFreshnessLabel` supprimé Phase 6 PR3 (N-2 résolu, audit grep
// confirmé 0 consumer). Si Phase 6.B re-requiert : git history pour récupérer.

export function freshnessColor(label) {
  switch (label) {
    case "fresh":
      return DS.green;
    case "ok":
      return DS.skyDeep;
    case "stale":
      return DS.amber;
    case "very_stale":
      return DS.charge;
    case "no_data":
    default:
      return DS.textLight;
  }
}

export function freshnessIcon(label) {
  switch (label) {
    case "fresh":
      return "🟢";
    case "ok":
      return "🔵";
    case "stale":
      return "🟠";
    case "very_stale":
      return "🔴";
    case "no_data":
    default:
      return "⚪";
  }
}

export function freshnessText(label) {
  switch (label) {
    case "fresh":
      return "Frais (< 1h)";
    case "ok":
      return "OK (< 6h)";
    case "stale":
      return "Périmé (< 24h)";
    case "very_stale":
      return "Très périmé (≥ 24h)";
    case "no_data":
    default:
      return "Pas de données";
  }
}
